import { Map } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { ScopePicker, type ScopeOption } from "@/components/layout/ScopePicker";
import { useMarketTopology } from "@/hooks/useMarketIntelligence";
import { marketRegionPath } from "@/lib/market-intelligence/paths";

import type { MarketCommodity } from "@/types/market-intelligence";

/**
 * The Market Intelligence region picker.
 *
 * Unlike the client picker there is no search query: the ISOs arrive in one
 * topology response, all present at once, so `ScopePicker` filters them
 * client-side and no `onSearch` is passed.
 *
 * The list comes from the SERVER, not from a constant. It used to read the
 * static `MARKET_ISOS`, which held seven entries and so offered SPP — a region
 * the feed publishes no curve, no settlement node and no index row for. Picking
 * it navigated to a region page that could only ever be empty. The topology
 * endpoint emits an ISO only when a hub of its own has a feed, so an
 * unpublishable region cannot appear here at all.
 *
 * `useMarketTopology` caches indefinitely and the Overview has already fetched
 * it by the time this renders, so this adds no request.
 *
 * Options are the code alone. Full names appear nowhere in this module: the
 * codes ARE the vocabulary here, and a second line repeating them in longhand
 * is noise in a short list.
 *
 * **One picker for both commodities.** Gas regions arrive in the same response
 * (`gasRegions`) and are picked the same way, so the two sidebars differ only
 * in which list fills this.
 */
export function RegionScopePicker({
  commodity = "power",
  selected,
  placeholder,
  onClose,
}: {
  commodity?: MarketCommodity;
  selected: { id: string; label: string } | null;
  placeholder: string;
  /** Closes the mobile drawer after a selection. */
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const { data: topology } = useMarketTopology();

  const regions = commodity === "gas" ? topology?.gasRegions : topology?.isos;
  const options: ScopeOption[] =
    regions?.map((region) => ({
      id: region.code,
      label: region.code,
    })) ?? [];

  return (
    <ScopePicker
      axis="Region"
      icon={Map}
      selected={selected}
      placeholder={placeholder}
      searchPlaceholder="Search regions…"
      emptyMessage="No regions match."
      options={options}
      onSelect={(code) => {
        onClose();
        navigate(marketRegionPath(commodity, code));
      }}
    />
  );
}
